
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreateOrganizationAtomicParams {
  organizationName: string;
  industry?: string | null;
  companySize?: string | null;
  location?: string | null;
  profileData?: {
    firstName: string;
    lastName: string;
    jobTitle: string;
    experienceLevel: string;
    goals: string;
  };
}

interface CreateOrganizationAtomicResult {
  success: boolean;
  organization_id?: string;
  organization_name?: string;
  error?: string;
  error_code?: string;
}

// Type guard to validate the RPC response
function isCreateOrganizationResult(data: unknown): data is CreateOrganizationAtomicResult {
  return (
    typeof data === 'object' &&
    data !== null &&
    !Array.isArray(data) &&
    'success' in data &&
    typeof (data as any).success === 'boolean'
  );
}

export const useCreateOrganizationAtomic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateOrganizationAtomicParams) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      console.log('Creating organization with params:', params);

      // Check if user already has an organization first
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (existingProfile?.organization_id) {
        console.log('User already has organization:', existingProfile.organization_id);
        return {
          success: false,
          error: 'User already belongs to an organization',
          error_code: 'ORGANIZATION_EXISTS'
        };
      }

      // Update profile first if profile data provided
      if (params.profileData) {
        console.log('Updating user profile with data:', params.profileData);
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            first_name: params.profileData.firstName,
            last_name: params.profileData.lastName,
            job_title: params.profileData.jobTitle,
            experience_level: params.profileData.experienceLevel,
            goals: params.profileData.goals
          })
          .eq('id', user.id);

        if (profileError) {
          console.error('Profile update error:', profileError);
          // Don't throw here, continue with organization creation
        }
      }

      // Use the atomic organization creation function
      console.log('Calling RPC function create_organization_atomic');
      const { data, error } = await supabase.rpc('create_organization_atomic', {
        p_user_id: user.id,
        p_organization_name: params.organizationName,
        p_industry: params.industry,
        p_company_size: params.companySize,
        p_location: params.location
      });

      if (error) {
        console.error('RPC error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      console.log('RPC response received:', data);

      // Validate the response structure before casting
      if (!isCreateOrganizationResult(data)) {
        console.error('Invalid RPC response structure:', data);
        throw new Error('Invalid response from organization creation function');
      }

      if (!data.success) {
        console.error('Organization creation failed:', data);
        
        // Handle specific error cases
        if (data.error?.includes('already belongs to an organization')) {
          return {
            success: false,
            error: 'You already belong to an organization',
            error_code: 'ORGANIZATION_EXISTS'
          };
        }
        
        throw new Error(data.error || 'Failed to create organization');
      }

      console.log('Organization created successfully:', data);
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(`Organization "${data.organization_name}" created successfully!`);
        
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: ['user-organization'] });
        queryClient.invalidateQueries({ queryKey: ['organization-status'] });
        queryClient.invalidateQueries({ queryKey: ['user-profile'] });
        queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      }
    },
    onError: (error) => {
      console.error('Organization creation error:', error);
      
      // Don't show toast for organization exists errors (handled in component)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (!errorMessage.includes('already belongs to an organization')) {
        toast.error(`Failed to create organization: ${errorMessage}`);
      }
    }
  });
};
